import { showNotification } from "./Notification";
import i18next from '../../i18n.ts';

const nonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  return trimmed || undefined;
};

const internalErrorMessages = new Set([
  'Invoice module operation failed',
  'Service not available. Please notify admin'
]);

const isInternalErrorMessage = (message: string): boolean =>
  internalErrorMessages.has(message);

const displayLegacyError = (error: unknown): string | undefined => {
  const errorValue =
    nonEmptyString(error) ||
    (typeof error === 'object' && error !== null
      ? nonEmptyString((error as { message?: unknown }).message)
      : undefined);

  if (!errorValue || isInternalErrorMessage(errorValue)) return undefined;

  const translationKey = `Backend:${errorValue}`;
  return i18next.exists(translationKey)
    ? i18next.t(translationKey)
    : errorValue;
};

export function getBackendErrorMessage(e: any): string | undefined {
  const responseData = e?.response?.data;
  const errors = responseData?.errors;

  if (errors !== undefined) {
    const legacyMessages = (Array.isArray(errors) ? errors : [errors])
      .map(displayLegacyError)
      .filter((message): message is string => !!message);
    return legacyMessages.length ? legacyMessages.join(' ') : undefined;
  }

  const responseMessage = nonEmptyString(responseData?.message);
  if (!responseMessage || isInternalErrorMessage(responseMessage)) {
    return undefined;
  }

  const responseStatus = e?.response?.status;
  const isActionableInvoiceModuleError =
    typeof responseData?.code === 'string' &&
    responseData.code.startsWith('invoice_module_') &&
    responseStatus >= 400 &&
    responseStatus < 500;
  const isProviderError = responseData?.code === 'cfdi_provider_error';
  const isLegacyInvoiceModuleClientError =
    responseData?.code === 'invoice_module_error' &&
    responseStatus >= 400 &&
    responseStatus < 500;

  if (isActionableInvoiceModuleError) {
    const translationKey = `Backend:${responseData.code}`;
    if (i18next.exists(translationKey)) {
      return i18next.t(translationKey);
    }
  }

  return isProviderError || isLegacyInvoiceModuleClientError
    ? responseMessage
    : undefined;
}

export function backendErrorsHandler(
  e: any,
  { showFallback = false }: { showFallback?: boolean } = {}
) {
  const backendMessage = getBackendErrorMessage(e);
  if (!backendMessage && !showFallback && !hasBackendErrors(e)) return;

  const message = backendMessage || i18next.t('Notifications:somethingWrong');
  showNotification({ type: 'Error', title: message });
}

export function hasBackendErrors(e: unknown): boolean {
  return Boolean(
    e &&
      typeof e === 'object' &&
      'response' in e &&
      (e as { response?: { data?: { errors?: unknown[] } } }).response?.data?.errors?.length
  );
}
